import React, { useState, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    LinearProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tabs,
    Tab,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    CloudUpload,
    Download as DownloadIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { UserContext } from '../contexts/UserContext';
import { showSuccess, showError } from '../utils/Alerts';

const BulkVehicleUpload = ({ open, handleClose, setVehicles }) => {
    const { token, user } = useContext(UserContext);
    const [tabValue, setTabValue] = useState(0);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Validate file type
            const validTypes = ['.csv', '.xlsx', '.xls'];
            const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

            if (!validTypes.includes(fileExtension)) {
                showError('Please upload a CSV or Excel file');
                return;
            }

            setFile(selectedFile);
            setResults(null);
        }
    };

    const handleUpload = () => {
        if (!file) {
            showError('Please select a file to upload');
            return;
        }

        setUploading(true);
        setProgress(0);

        // Simulate file processing with mock data
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 90) {
                    clearInterval(progressInterval);
                    return 100;
                }
                return prev + 10;
            });
        }, 200);

        setTimeout(() => {
            clearInterval(progressInterval);
            setProgress(100);

            // Mock successful result
            const mockResults = {
                successCount: 5,
                errorCount: 0,
                errors: [],
                vehicles: [
                    {
                        vehicleID: `VEH-${Date.now()}-1`,
                        vin: '1HGCM82633A123456',
                        make: 'Honda',
                        model: 'Accord',
                        year: 2021,
                        price: 25000,
                        status: 'available',
                        dateAdded: new Date().toISOString(),
                    },
                    // Add more mock vehicles as needed
                ],
            };

            setResults(mockResults);

            if (mockResults.successCount > 0) {
                showSuccess(
                    tabValue === 0
                        ? `Successfully uploaded ${mockResults.successCount} vehicles`
                        : `Successfully updated ${mockResults.successCount} vehicles`
                );
                if (mockResults.vehicles) {
                    setVehicles(prev => [...prev, ...mockResults.vehicles]);
                }
            }

            setUploading(false);
        }, 2000);
    };

    const handleDownloadTemplate = () => {
        // Create CSV template
        const headers = [
            'vin',
            'make',
            'model',
            'year',
            'trim',
            'bodyType',
            'mileage',
            'price',
            'costPrice',
            'exteriorColor',
            'interiorColor',
            'transmission',
            'fuelType',
            'engineSize',
            'cylinders',
            'drivetrain',
            'doors',
            'seats',
            'condition',
            'status',
            'features',
            'description',
        ];

        const sampleData = [
            '1HGBH41JXMN109186',
            'Honda',
            'Accord',
            '2021',
            'EX-L',
            'Sedan',
            '25000',
            '28500',
            '26000',
            'Silver',
            'Black',
            'Automatic',
            'Gasoline',
            '2.0L',
            '4',
            'FWD',
            '4',
            '5',
            'Used',
            'available',
            'Leather Seats, Sunroof, Navigation, Backup Camera',
            'Well-maintained Honda Accord with low mileage',
        ];

        const csvContent = [
            headers.join(','),
            sampleData.join(','),
            // Add empty row for user to fill
            headers.map(() => '').join(','),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = tabValue === 0 ? 'vehicle_upload_template.csv' : 'vehicle_update_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess('Template downloaded successfully');
    };

    const handleCloseModal = () => {
        setFile(null);
        setResults(null);
        setProgress(0);
        handleClose();
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setFile(null);
        setResults(null);
        setProgress(0);
    };

    return (
        <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
            <DialogTitle>
                Bulk Vehicle Operations
                <IconButton
                    aria-label="close"
                    onClick={handleCloseModal}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
                    <Tab label="Bulk Upload (New Vehicles)" />
                    <Tab label="Bulk Update (Existing Vehicles)" />
                </Tabs>

                {/* Instructions */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {tabValue === 0 ? 'Upload New Vehicles' : 'Update Existing Vehicles'}
                    </Typography>
                    <Typography variant="body2">
                        {tabValue === 0
                            ? '1. Download the CSV template\n2. Fill in vehicle details\n3. Upload the completed file'
                            : '1. Download the update template\n2. Include VIN for vehicles to update\n3. Modify fields you want to change\n4. Upload the file'}
                    </Typography>
                </Alert>

                {/* Download Template */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        fullWidth
                    >
                        Download CSV Template
                    </Button>
                </Box>

                {/* File Upload */}
                <Box
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        mb: 3,
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    <input
                        type="file"
                        id="bulk-upload-file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="bulk-upload-file" style={{ cursor: 'pointer' }}>
                        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {file ? file.name : 'Click to select file or drag and drop'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Supported formats: CSV, Excel (.xlsx, .xls)
                        </Typography>
                    </label>
                </Box>

                {/* Progress */}
                {uploading && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" gutterBottom>
                            Processing... {progress}%
                        </Typography>
                        <LinearProgress variant="determinate" value={progress} />
                    </Box>
                )}

                {/* Results */}
                {results && (
                    <Box sx={{ mt: 3 }}>
                        <Alert
                            severity={results.errorCount === 0 ? 'success' : 'warning'}
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="subtitle2">
                                Operation completed: {results.successCount} successful, {results.errorCount} failed
                            </Typography>
                        </Alert>

                        {results.errors && results.errors.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom color="error">
                                    Errors:
                                </Typography>
                                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Row</TableCell>
                                                <TableCell>VIN</TableCell>
                                                <TableCell>Error</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {results.errors.map((error, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{error.row}</TableCell>
                                                    <TableCell>{error.vin || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="error">
                                                            {error.message}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {results.warnings && results.warnings.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom color="warning.main">
                                    Warnings:
                                </Typography>
                                {results.warnings.map((warning, index) => (
                                    <Alert key={index} severity="warning" sx={{ mb: 1 }}>
                                        Row {warning.row}: {warning.message}
                                    </Alert>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Field Guide */}
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Required Fields:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip label="VIN" size="small" color="primary" />
                        <Chip label="Make" size="small" color="primary" />
                        <Chip label="Model" size="small" color="primary" />
                        <Chip label="Year" size="small" color="primary" />
                        <Chip label="Price" size="small" color="primary" />
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                        Status Values:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip label="available" size="small" />
                        <Chip label="reserved" size="small" />
                        <Chip label="sold" size="small" />
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>
                        Condition Values:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip label="New" size="small" />
                        <Chip label="Used" size="small" />
                        <Chip label="Certified Pre-Owned" size="small" />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseModal}>Cancel</Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!file || uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                >
                    {uploading ? 'Processing...' : tabValue === 0 ? 'Upload Vehicles' : 'Update Vehicles'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkVehicleUpload;
